import React, { useRef, useCallback, useEffect } from 'react';

interface CameraViewProps {
  cameraMode: 'photo' | 'video';
  facingMode: 'user' | 'environment';
  isRecording: boolean;
  recordingTime: number;
  onTakePhoto: () => void;
  onStartRecording: () => void;
  onStopRecording: () => void;
  onSwitchCamera: () => void;
  onToggleMode: () => void;
  onGallerySelect: () => void;
  onCameraInit: (stream: MediaStream) => void;
  onCameraStop: () => void;
}

const CameraView: React.FC<CameraViewProps> = ({
  cameraMode,
  facingMode,
  isRecording,
  recordingTime,
  onTakePhoto,
  onStartRecording,
  onStopRecording,
  onSwitchCamera,
  onToggleMode,
  onGallerySelect,
  onCameraInit,
  onCameraStop,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const initCamera = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: facingMode },
        audio: cameraMode === 'video',
      });

      streamRef.current = stream;
      onCameraInit(stream);

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (error) {
      console.error('Camera access error:', error);
    }
  }, [cameraMode, facingMode, onCameraInit]);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
      onCameraStop();
    }
  }, [onCameraStop]);

  useEffect(() => {
    initCamera();
    return () => {
      stopCamera();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameraMode, facingMode]);

  return (
    <div className="relative w-full h-full bg-black">
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        className={`w-full h-full object-cover ${facingMode === 'user' ? 'scale-x-[-1]' : ''}`}
      />

      {/* Camera Controls */}
      <div className="absolute bottom-20 left-0 right-0 flex items-center justify-center gap-4">
        {/* Gallery shortcut */}
        <button
          onClick={onGallerySelect}
          className="w-12 h-12 bg-white bg-opacity-20 backdrop-blur-md rounded-full flex items-center justify-center hover:bg-opacity-30 transition-all"
        >
          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        </button>

        {/* Capture Button */}
        <button
          onClick={cameraMode === 'photo' ? onTakePhoto : isRecording ? onStopRecording : onStartRecording}
          className={`w-20 h-20 rounded-full border-4 border-white flex items-center justify-center transition-all ${
            isRecording ? 'bg-red-500 animate-pulse' : 'bg-white bg-opacity-20 backdrop-blur-md'
          }`}
        >
          {cameraMode === 'video' && isRecording ? (
            <div className="w-6 h-6 bg-white rounded-sm"></div>
          ) : (
            <div className={`w-14 h-14 rounded-full ${cameraMode === 'photo' ? 'bg-white' : 'bg-red-500'}`}></div>
          )}
        </button>

        {/* Mode Toggle */}
        <button
          onClick={onToggleMode}
          className="w-12 h-12 bg-white bg-opacity-20 backdrop-blur-md rounded-full flex items-center justify-center hover:bg-opacity-30 transition-all"
        >
          {cameraMode === 'photo' ? (
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
          ) : (
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
            </svg>
          )}
        </button>
      </div>

      {/* Camera Switch Button */}
      <div className="absolute top-20 right-4">
        <button
          onClick={onSwitchCamera}
          disabled={isRecording}
          className="w-12 h-12 bg-white bg-opacity-20 backdrop-blur-md rounded-full flex items-center justify-center disabled:opacity-50 hover:bg-opacity-30 transition-all"
        >
          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        </button>
      </div>

      {/* Recording Timer */}
      {isRecording && (
        <div className="absolute top-20 left-1/2 transform -translate-x-1/2 bg-red-500 text-white px-4 py-2 rounded-full flex items-center gap-2">
          <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
          <span className="font-mono">
            {Math.floor(recordingTime / 60)}:{(recordingTime % 60).toString().padStart(2, '0')}
          </span>
        </div>
      )}
    </div>
  );
};

export default CameraView;

